import assert from 'assert';
import {parse} from 'graphql';
import {print as astToString} from 'query-api-graphql';
import {
  FieldNode,
  InlineFragmentNode,
  Kind,
  OperationDefinitionNode,
  SelectionNode,
  SelectionSetNode,
} from 'query-api-graphql';

import buildQueryApiQueryAst from 'analysis-tool/data-frame-creation/schemas/buildQueryApiQueryAst';
// eslint-disable-next-line import-access/jsdoc
import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import {getQueryDefinition} from 'graphql-client/AST';
import {buildAnyOfFilterValue} from 'query-api/QueryApiFilters';
import {QueryApiIntrospection} from 'query-api/useQueryApiIntrospection';

export const DEFAULT_QUERY_FORM_STATE = {entityListingFieldName: null};
export const EMPTY_FIELD_SELECTIONS = {
  kind: Kind.SELECTION_SET,
  // TODO: Select default fields for the schema according to lookupBy
  selections: [],
} as SelectionSetNode;

/**
 * Builds a GraphQL query string from the given form state.
 * This function is the reverse operation of `buildInitialQueryFormStateFromQueryString`.
 *
 * @param formState - The form state containing entity listing field name, filters, and field selections
 * @returns A GraphQL query string representation, or null if the query AST cannot be built
 */
export function buildQueryStringFromFormState(formState: FormState): string | null {
  const queryAst = buildQueryApiQueryAst(formState);
  return queryAst != null ? astToString(queryAst) : null;
}

/**
 * Builds an initial query form state from a schema ID and query string.
 * This function is the reverse operation of `buildQueryStringFromFormState`.
 * Does not set filters in the form state.
 *
 * @param maybeSchemaId - The schema ID to build the form state for, or null
 * @param maybeQueryString - The GraphQL query string to parse, or null
 * @param queryApiIntrospection - The Query API introspection data
 * @returns A FormState object with parsed entity listing field name, filters, and field selections
 */
export function buildInitialQueryFormStateFromQueryString(
  maybeSchemaId: string | null,
  maybeQueryString: string | null,
  queryApiIntrospection: QueryApiIntrospection
): FormState {
  if (maybeSchemaId == null) {
    return DEFAULT_QUERY_FORM_STATE;
  }

  const listingField = queryApiIntrospection.getQueryListingFieldForSchema(maybeSchemaId);
  assert(listingField != null, 'User selected a schema without a corresponding Query API listing field');

  return {
    entityListingFieldName: listingField.name,
    entityFilterArguments: [
      {
        kind: Kind.ARGUMENT,
        name: {
          kind: Kind.NAME,
          value: 'id',
        },
        value: buildAnyOfFilterValue([]),
      },
    ],
    entityFieldSelections: parseQueryStringToSelectionSet(maybeQueryString),
    relatedResultsFormStates: [],
  };
}

/**
 * Parses a GraphQL query string and extracts the field selections as a SelectionSetNode.
 *
 * The parsing makes several assumptions about the structure and content of the input query:
 *
 * 1. **Specific query shape requiring unwrapping**: The queries we parse will be of the following
 *    shape that needs to be unwrapped before we can parse the field selections. The outer structure
 *    is a query operation with one root field selection (e.g., `plasmidItems(id: {anyOf: []}) { ... }`).
 *    We need to extract the SelectionSet inside that root field to get the actual field selections
 *    we want to preserve.
 *
 * 2. **No FragmentSpreadNode expected**: We don't expect to see FragmentSpreadNode in the queries
 *    built for data frame creation from schema field selection. This same assumption is made in
 *    the data frame creation util `buildSelectionString` and the query api util `countSelectedFields`.
 *
 * 3. **Inline Fragment nodes always have typeCondition**: The way we generate queries for data frame
 *    creation via schema field selection (vs for directive application) means that Inline Fragment
 *    nodes will always have a typeCondition.
 *
 * @param queryString - The GraphQL query string to parse, or null
 * @returns A SelectionSetNode containing the parsed field selections, or empty selections if input is null
 */
function parseQueryStringToSelectionSet(queryString: string | null): SelectionSetNode {
  if (queryString == null) {
    return EMPTY_FIELD_SELECTIONS;
  }

  const ast = parse(queryString);
  // Parse the query string given its expected shape like plasmidItems(id: {anyOf: []}) { ... }
  const queryDefinition = getQueryDefinition(ast) as OperationDefinitionNode;
  const firstSelection = queryDefinition?.selectionSet?.selections?.[0];

  if (firstSelection && firstSelection.kind !== Kind.FRAGMENT_SPREAD && firstSelection.selectionSet) {
    return buildFormattedSelectionSet(firstSelection.selectionSet);
  }

  // We should never get here, because all persisted queries should be of the expected shape,
  // and even if previously selected fields no longer exist (ex: if you change the entity type),
  // that doesn't impact our ability to parse the query string.
  throw new Error('Previously selected fields could not be resolved.');
}

function buildFormattedSelectionSet(node: SelectionSetNode): SelectionSetNode {
  return {
    kind: Kind.SELECTION_SET,
    selections: node.selections.map((selection) => buildFormattedNode(selection)),
  };
}

function buildFormattedNode(node: SelectionNode): FieldNode | InlineFragmentNode {
  const {kind} = node;
  switch (kind) {
    case Kind.FIELD: {
      const {selectionSet} = node;
      return {
        kind,
        name: {kind: Kind.NAME, value: node.name.value},
        ...(selectionSet && {
          selectionSet: buildFormattedSelectionSet(selectionSet),
        }),
      };
    }

    case Kind.INLINE_FRAGMENT: {
      const {selectionSet, typeCondition} = node;
      return {
        kind,
        typeCondition: typeCondition && {
          kind: Kind.NAMED_TYPE,
          name: {kind: Kind.NAME, value: typeCondition.name.value},
        },
        ...(selectionSet && {
          selectionSet: buildFormattedSelectionSet(selectionSet),
        }),
      };
    }

    default: {
      // We don't expect to see any other selection kinds in the queries we store for LookUp steps
      throw new Error(`Unhandled selection kind: ${kind}`);
    }
  }
}
